import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { collectionGroup, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import { CalendarDays, Lock, SearchX } from "lucide-react";
import { db } from "../firebase/config";
import { MeetDetailsPage } from "../meets/MeetDetailsPage";
import { DualMeetPage } from "../meets/DualMeetPage";
import { EventDetailsPage } from "../events/EventDetailsPage";
import { publicTeamSectionUrl } from "./publicTeamNavigation";
function meetFromData(meetId, data) {
    const location = typeof data.location === "string"
        ? data.location
        : data.location?.name ?? data.address ?? "Location TBD";
    const entries = typeof data.entries === "number"
        ? data.entries
        : Array.isArray(data.entries) ? data.entries.length : 0;
    return {
        id: String(data.id ?? meetId),
        name: String(data.name ?? "Untitled meet"),
        date: new Date(Number(data.dateTime ?? 0)),
        location,
        type: String(data.type ?? "Invitational"),
        entries,
        course: data.course,
        opponent: data.opponent,
        score: data.score,
    };
}
function seasonFromTeam(team, seasonId) {
    const season = team.seasons?.[seasonId];
    return season ? { ...season, id: season.id || seasonId } : null;
}
export function PublicMeetPage({ meetId }) {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [view, setView] = useState({ page: "meet" });
    useEffect(() => {
        let active = true;
        async function loadMeet() {
            setLoading(true);
            setError("");
            setState(null);
            try {
                const meetSnapshot = await getDocs(query(collectionGroup(db, "schedule"), where("id", "==", meetId), limit(1)));
                const meetDocument = meetSnapshot.docs[0];
                const teamRef = meetDocument?.ref.parent.parent;
                if (!meetDocument || !teamRef) {
                    if (active)
                        setError("This meet could not be found.");
                    return;
                }
                const teamSnapshot = await getDoc(doc(db, "teams", teamRef.id));
                if (!teamSnapshot.exists()) {
                    if (active)
                        setError("This meet could not be found.");
                    return;
                }
                const team = { id: teamSnapshot.id, ...teamSnapshot.data() };
                if (team.public !== true) {
                    if (active)
                        setError("This meet is not available for public viewing.");
                    return;
                }
                const meetData = meetDocument.data();
                const seasonId = String(meetData.seasonId ?? "");
                const season = seasonFromTeam(team, seasonId);
                if (!season) {
                    if (active)
                        setError("The season for this meet could not be found.");
                    return;
                }
                if (active)
                    setState({ team, season, meet: meetFromData(meetDocument.id, meetData) });
            }
            catch (caughtError) {
                if (active)
                    setError(caughtError instanceof Error ? caughtError.message : "This meet could not be loaded.");
            }
            finally {
                if (active)
                    setLoading(false);
            }
        }
        void loadMeet();
        return () => {
            active = false;
        };
    }, [meetId]);
    if (loading) {
        return _jsx("main", { className: "main-standalone", children: _jsx("div", { className: "content", children: _jsxs("div", { className: "details-state", children: [_jsx(CalendarDays, { size: 24 }), _jsx("strong", { children: "Loading meet..." })] }) }) });
    }
    if (error || !state) {
        return _jsx("main", { className: "main-standalone", children: _jsx("div", { className: "content", children: _jsxs("div", { className: "details-state details-state--error", children: [error.includes("public") ? _jsx(Lock, { size: 24 }) : _jsx(SearchX, { size: 24 }), _jsx("strong", { children: "Meet unavailable" }), _jsx("span", { children: error || "This meet could not be loaded." })] }) }) });
    }
    const publicContext = { team: state.team, season: state.season };
    if (view.page === "score")
        return _jsx("main", { className: "main-standalone", children: _jsx(DualMeetPage, { meet: state.meet, publicContext: publicContext, onBack: () => setView({ page: "meet" }) }) });
    if (view.page === "event")
        return _jsx("main", { className: "main-standalone", children: _jsx(EventDetailsPage, { meet: state.meet, event: view.event, publicContext: publicContext, onBack: () => setView({ page: "meet" }) }) });
    return (_jsx("main", { className: "main-standalone", children: _jsx(MeetDetailsPage, { meet: state.meet, publicContext: publicContext, readOnly: true, showBackButton: true, onBack: () => { window.location.href = publicTeamSectionUrl(state.team.slug, "Schedule", state.season.id); }, onSelectEvent: (event) => setView({ page: "event", event }), onViewScore: () => setView({ page: "score" }) }) }));
}
